import { useCallback } from 'react';
import Editor, { type OnMount, type BeforeMount } from '@monaco-editor/react';
import { Loader2 } from 'lucide-react';
import { useThemeStore } from '@/store/theme';

interface SqlEditorProps {
  value: string;
  onChange: (value: string) => void;
  /** Ctrl/Cmd + Enter bosilganda. */
  onRun?: () => void;
}

export function SqlEditor({ value, onChange, onRun }: SqlEditorProps) {
  const theme = useThemeStore((s) => s.theme);
  const monacoTheme = theme === 'dark' ? 'sqluz-dark' : 'vs';

  const beforeMount: BeforeMount = useCallback((monaco) => {
    monaco.editor.defineTheme('sqluz-dark', {
      base: 'vs-dark',
      inherit: true,
      rules: [
        { token: 'keyword.sql', foreground: 'c792ea' },
        { token: 'operator.sql', foreground: '89ddff' },
        { token: 'string.sql', foreground: 'c3e88d' },
        { token: 'number.sql', foreground: 'f78c6c' },
        { token: 'comment', foreground: '5c6370', fontStyle: 'italic' },
      ],
      colors: {
        'editor.background': '#0d1117',
        'editor.lineHighlightBackground': '#161b22',
        'editorLineNumber.foreground': '#484f58',
        'editorCursor.foreground': '#6366f1',
        'editor.selectionBackground': '#264f78',
      },
    });
  }, []);

  const onMount: OnMount = useCallback(
    (editor, monaco) => {
      editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter, () => {
        onRun?.();
      });
    },
    [onRun],
  );

  return (
    <Editor
      height="100%"
      defaultLanguage="sql"
      value={value}
      theme={monacoTheme}
      beforeMount={beforeMount}
      onMount={onMount}
      onChange={(v) => onChange(v ?? '')}
      loading={
        <div className="flex h-full items-center justify-center text-content-muted">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      }
      options={{
        fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
        fontSize: 14,
        lineHeight: 22,
        minimap: { enabled: false },
        scrollBeyondLastLine: false,
        smoothScrolling: true,
        padding: { top: 14, bottom: 14 },
        tabSize: 2,
        automaticLayout: true,
        renderLineHighlight: 'line',
        cursorBlinking: 'smooth',
        fixedOverflowWidgets: true,
        scrollbar: { verticalScrollbarSize: 8, horizontalScrollbarSize: 8 },
      }}
    />
  );
}
